/**
 * Child-side funds workflow (NOS-171): fiscal projects see their fund balance,
 * request disbursements from their sponsor, and track/cancel requests.
 */
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/lib/supabase"
import { usePermission } from "@/hooks/usePermission"
import { useDisbursementRequests } from "@/hooks/useDisbursementRequests"
import { formatCurrencyCents } from "@/lib/sponsor"

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-600",
}

export function FundRequestsCard({ orgId }: { orgId: string }) {
  const { hasPermission } = usePermission("can_view_financials")
  const [sponsorshipId, setSponsorshipId] = useState<string | null>(null)
  const [balanceCents, setBalanceCents] = useState(0)
  const [amount, setAmount] = useState("")
  const [purpose, setPurpose] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const requests = useDisbursementRequests(sponsorshipId ? { childOrgId: orgId } : null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data: link } = await supabase
        .from("sponsorships")
        .select("id, mode, status")
        .eq("child_org_id", orgId)
        .maybeSingle()
      if (cancelled) return
      const fiscal =
        link?.mode === "fiscal" && (link?.status === "active" || link?.status === "transferring")
      setSponsorshipId(fiscal ? link!.id : null)
      if (fiscal) {
        const { data: balance } = await supabase.rpc("get_fund_balance", {
          p_sponsorship_id: link!.id,
        })
        if (!cancelled) setBalanceCents(Number(balance ?? 0))
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [orgId, requests.requests.length])

  if (!sponsorshipId || !hasPermission) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const dollars = Number.parseFloat(amount)
    if (Number.isNaN(dollars) || dollars <= 0) {
      setFormError("Enter a positive amount")
      return
    }
    const cents = Math.round(dollars * 100)
    if (cents > balanceCents) {
      setFormError(
        `Request exceeds your current fund balance (${formatCurrencyCents(balanceCents)})`
      )
      return
    }
    if (!purpose.trim()) {
      setFormError("Describe what the funds are for")
      return
    }
    setFormError(null)
    setBusy(true)
    const { error } = await requests.submit({
      sponsorshipId,
      childOrgId: orgId,
      amountCents: cents,
      purpose,
    })
    setBusy(false)
    if (error) {
      setFormError(error.message)
      return
    }
    toast.success("Request sent to your sponsor")
    setAmount("")
    setPurpose("")
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Your fund</CardTitle>
        <CardDescription>
          Balance held by your fiscal sponsor:{" "}
          <strong className="text-foreground">{formatCurrencyCents(balanceCents)}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="fr-amount">Amount ($)</Label>
            <Input
              id="fr-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-32"
              disabled={busy}
            />
          </div>
          <div className="min-w-48 flex-1 space-y-1">
            <Label htmlFor="fr-purpose">Purpose</Label>
            <Input
              id="fr-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Q3 program supplies"
              disabled={busy}
            />
          </div>
          <Button type="submit" size="sm" disabled={busy}>
            {busy ? "Sending..." : "Request funds"}
          </Button>
        </form>
        {formError && <p className="text-sm text-destructive">{formError}</p>}

        {requests.requests.length > 0 && (
          <ul className="divide-y rounded-md border">
            {requests.requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                <span className="font-medium tabular-nums">{formatCurrencyCents(r.amount)}</span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{r.purpose}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[r.status]}`}
                >
                  {r.status}
                </span>
                {r.decision_note && (
                  <span className="w-full text-xs text-muted-foreground">
                    Note: {r.decision_note}
                  </span>
                )}
                {r.status === "pending" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const { error } = await requests.cancel(r.id)
                      if (error) toast.error(error.message)
                      else toast.success("Request cancelled")
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
